/**
 * base-tooltip の吹き出しを hover / click(タップ) / キーボードfocus / Esc / 外側クリックで表示制御する
 * フレームワーク非依存のコア。内側ラッパ(.anglrC-tooltip)へ状態機械で .is-visible をトグルする
 * （既定の非表示は [rich-tooltip-trigger] 側のCSSが担う）。解除用の teardown を返す。
 *
 * 表示条件 = pinned || keyboardFocus || (hovering && !suppressHover)
 */
type RichTooltipState = {
  pinned: boolean;
  keyboardFocus: boolean;
  hovering: boolean;
  suppressHover: boolean; // click で閉じた直後の hover 再表示を抑止（mouseleave で解除）
};

// :focus-visible 未対応ブラウザでは matches が例外を投げるため、その場合はキーボード由来として扱う
function isFocusVisible(el: Element): boolean {
  try {
    return el.matches(':focus-visible');
  } catch {
    return true;
  }
}

export function bindRichTooltip(root: HTMLElement | null): () => void {
  const anchor = root ? root.querySelector<HTMLElement>('.anglrC-tooltip') : null;
  if (!root || !anchor) {
    return () => {};
  }
  const trigger: Element = anchor.querySelector('button, a, [tabindex]') || anchor;

  const state: RichTooltipState = { pinned: false, keyboardFocus: false, hovering: false, suppressHover: false };
  const isVisible = (): boolean => state.pinned || state.keyboardFocus || (state.hovering && !state.suppressHover);
  const apply = (changes: Partial<RichTooltipState>): void => {
    Object.assign(state, changes);
    anchor.classList.toggle('is-visible', isVisible());
  };

  const bindings: Array<[EventTarget, string, (event: Event) => void]> = [
    [trigger, 'mouseenter', () => apply({ hovering: true })],
    [trigger, 'mouseleave', () => apply({ hovering: false, suppressHover: false })],
    [
      trigger,
      'click',
      (event: Event) => {
        event.preventDefault(); // 画面遷移させない
        event.stopPropagation();
        apply(state.pinned ? { pinned: false, suppressHover: true, keyboardFocus: false } : { pinned: true });
      },
    ],
    [
      trigger,
      'focus',
      () => {
        // :focus-visible（キーボード由来）のみ表示。マウスの focus は除外
        if (isFocusVisible(trigger)) {
          apply({ keyboardFocus: true });
        }
      },
    ],
    [
      trigger,
      'blur',
      () => {
        if (state.keyboardFocus) {
          apply({ keyboardFocus: false, pinned: false });
        }
      },
    ],
    [
      document,
      'click',
      (event: Event) => {
        const target = event.target as Node | null;
        if ((!target || !root.contains(target)) && isVisible()) {
          apply({ pinned: false, hovering: false, keyboardFocus: false });
        }
      },
    ],
    [
      document,
      'keydown',
      (event: Event) => {
        if ((event as KeyboardEvent).key === 'Escape' && isVisible()) {
          apply({ pinned: false, keyboardFocus: false, suppressHover: true });
        }
      },
    ],
  ];

  bindings.forEach(([el, type, fn]) => el.addEventListener(type, fn));
  return () => bindings.forEach(([el, type, fn]) => el.removeEventListener(type, fn));
}
